import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, X } from "lucide-react";
import { listLookupValues } from "../../../../../Services/adminCorporates";
import { Input } from "../../../../../ui/input";
import { Button } from "../../../../../ui/button";
import { Skeleton } from "../../../../../ui/skeleton";
import { extractApiError } from "./utils";

/**
 * Multi-select for benefit signals. Backed by the corporate's
 * lookup-values?kind=benefit-signals list. Reload exposed via `reloadKey`
 * so callers can re-fetch after a 422 ("signal no longer in catalog") error.
 */
const BenefitSignalPicker = ({
  corporateId,
  value = [],
  onChange,
  reloadKey,
  disabled = false,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    if (!corporateId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await listLookupValues(corporateId, "benefit-signals", {
        active_only: false,
      });
      setItems(data?.items || []);
    } catch (err) {
      setLoadError(extractApiError(err, "Failed to load signals."));
    } finally {
      setLoading(false);
    }
  }, [corporateId]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => (it.name || "").toLowerCase().includes(q));
  }, [items, query]);

  const togglePick = (signalId) => {
    if (disabled) return;
    const next = selectedSet.has(signalId)
      ? value.filter((v) => v !== signalId)
      : [...value, signalId];
    onChange?.(next);
  };

  const selectedItems = useMemo(
    () => items.filter((it) => selectedSet.has(it.id)),
    [items, selectedSet]
  );

  return (
    <div className="grid gap-2">
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedItems.map((it) => (
            <span
              key={it.id}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
                it.is_active === false
                  ? "bg-muted text-muted-foreground line-through"
                  : "bg-primary/10 text-primary"
              }`}
            >
              {it.name || it.id}
              <button
                type="button"
                onClick={() => togglePick(it.id)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={`Remove ${it.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <Input
        placeholder={
          items.length === 0
            ? "No signals in catalog — add some first."
            : "Search signals…"
        }
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={disabled || items.length === 0}
      />

      {loadError && (
        <div className="flex items-center justify-between text-xs text-destructive">
          <span>{loadError}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={load}
            className="h-6 px-2"
          >
            Retry
          </Button>
        </div>
      )}

      {loading ? (
        <div className="space-y-1.5">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? null : (
        <div className="max-h-48 overflow-y-auto rounded-md border divide-y">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              No matching signals.
            </div>
          ) : (
            filtered.map((it) => {
              const picked = selectedSet.has(it.id);
              return (
                <button
                  type="button"
                  key={it.id}
                  onClick={() => togglePick(it.id)}
                  disabled={disabled}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/50 ${
                    picked ? "bg-muted/40" : ""
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span
                      className={`flex h-4 w-4 items-center justify-center rounded border ${
                        picked
                          ? "bg-primary border-primary text-primary-foreground"
                          : "border-input"
                      }`}
                    >
                      {picked && <Check className="h-3 w-3" />}
                    </span>
                    <span
                      className={
                        it.is_active === false
                          ? "text-muted-foreground line-through"
                          : ""
                      }
                    >
                      {it.name}
                    </span>
                  </span>
                  {it.is_active === false && (
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Inactive
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default BenefitSignalPicker;
